import React, { useEffect, useState, useRef } from "react";
import Select, {  SingleValue } from "react-select";
import { IFloatingFilterParams } from "ag-grid-community";
import { useDataTableContext } from "../shared/Context";
const dropdownRoot = typeof document !== 'undefined' ? document.getElementById('dropdown-root') : null;

interface ICustomDropdownFloatingFilterProps extends IFloatingFilterParams {
  defaultFilterValue?: string[];
  values?: any[] | (() => Promise<any[]>);
  name?: string;
}

const CustomDropdownFloatingFilter = (props: ICustomDropdownFloatingFilterProps) => {
  const { floatingFilterValueMap } = useDataTableContext();

  const { parentFilterInstance, api, column, defaultFilterValue = [], values = [], name } = props;

  const colId = column.getColId();
  const placeholderName = column.getColDef().headerName;

  const floatingFilterValueMapKey = `${name}${colId}`;
  const initialValue = floatingFilterValueMap.current.get(floatingFilterValueMapKey) ?? defaultFilterValue[0] ??  "";
  const [selectedValue, setSelectedValue] = useState(initialValue);
  const [options, setOptions] = useState<{ label: string; value: string }[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Fetch options + prepend "All"
  useEffect(() => {
    const fetchOptions = async () => {
      let resolvedOptions: { label: string; value: string }[] = [];
      if (typeof values === "function") {
        const result = await values();
        resolvedOptions = result.map((v: any) => ({ label: v.label ?? v.value, value: v.value }));
      } else {
        resolvedOptions = values.map((v: any) => ({ label: v.label ?? v.value, value: v.value }));
      }
      // Prepend "All" option
      setOptions([{ label: "All", value: "" }, ...resolvedOptions]);
    };
    fetchOptions();
  }, [values]);

  // Lightweight measurement: only measure the control width when the menu opens.
  // This avoids expensive per-option DOM measurement while keeping the menu at
  // least the control width and clamped to the viewport. Long labels will wrap.

  // Initialize filter
  useEffect(() => {
    if (initialValue !== "") {
      floatingFilterValueMap.current.set(floatingFilterValueMapKey, initialValue);
      parentFilterInstance((filterInstance) => {
        filterInstance.setModel({ type: "equals", filter: initialValue });
      });
    }
  }, []);
  // Sync selected value when options load
  useEffect(() => {
    const val = floatingFilterValueMap.current.get(floatingFilterValueMapKey) ?? "";
    setSelectedValue(val);
  }, [options]);

  const handleChange = (option: SingleValue<{ label: string; value: string }>) => {
    const value = option?.value ?? "";
    setSelectedValue(value);
    parentFilterInstance((filter) => {
      filter.setModel(value ? { type: "equals", filter: value } : null);
      floatingFilterValueMap.current.set(floatingFilterValueMapKey, value);
      api.onFilterChanged();
    });
  };

  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      <Select
        value={selectedValue === "" ? null : options.find((opt) => opt.value === selectedValue)}
        onChange={handleChange}
        options={options}
        placeholder={`Select ${placeholderName}`}
        isSearchable
        menuPlacement="bottom"
        menuPortalTarget={dropdownRoot}
        filterOption={(option, inputValue) => {
          if (!inputValue) return true;
          return option?.label?.toLowerCase().includes(inputValue?.toLowerCase());
        }}
        styles={{
          container: (base) => ({ ...base, width: "100%", minHeight: 24, overflow: "hidden" }),
          control: (base) => ({ ...base, minHeight: 24, height: 24, fontSize: 12, cursor: "pointer", overflow: "hidden",
              border: "1px solid #0000006c",
              borderRadius: 4,
           }),
          valueContainer: (base) => ({ ...base, padding: "0 10px", overflow: "hidden" }),
          input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }),
          placeholder: (base) => ({
            ...base,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: "100%",
          }),
          indicatorsContainer: (base) => ({ ...base, height: 24 }),
          dropdownIndicator: (base) => ({ ...base, padding: 2 }),
          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
          menu: (base) => ({
            ...base,
            width: "auto",
            minWidth: 100,
            maxWidth: 500,
            overflow: "visible",
          }),
          option: (base, state) => ({
            ...base,
            fontSize: 12,
            padding: "4px 10px",
            backgroundColor: state.isSelected ? "#e6f7ff" : state.isFocused ? "#f0f0f0" : "white",
            color: "black",
            cursor: "pointer",
            width: "100%",
            // allow wrapping to next line when option text is long
            overflow: "visible",
            textOverflow: "unset",
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "break-word",
          }),
          singleValue: (base) => ({ ...base, fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%" }),
        }}
      />
    </div>
  );
};


export default CustomDropdownFloatingFilter;